import React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
} from "@material-ui/core";
import { green, orange, grey } from "@material-ui/core/colors";
import { Check, Delete, Edit, Warning, ChevronLeft } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ReferralItem, computeValidationIssues } from "./types";
import { HFlex, ProgressButton, Text } from "shared";
import { Send } from "assets";

interface Props {
  items: ReferralItem[];
  onRemove: (id: string) => void;
  onEditItem: (index: number) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  submittedCount?: number;
  totalToSubmit?: number;
}

const Container = styled(Paper)`
  padding: 24px;
`;

const StyledTableRow = styled(TableRow)<{ $hasWarning?: boolean; $clickable?: boolean }>`
  background: ${({ $hasWarning }) => ($hasWarning ? orange[50] : "inherit")};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  &:hover {
    background: ${({ $hasWarning, $clickable }) =>
      $clickable ? ($hasWarning ? orange[100] : grey[100]) : $hasWarning ? orange[50] : "inherit"};
  }
`;

export const BatchSummary: React.VFC<Props> = ({
  items,
  onRemove,
  onEditItem,
  onBack,
  onSubmit,
  isSubmitting,
  submittedCount = 0,
  totalToSubmit = 0,
}) => {
  const { t } = useTranslation();
  const getItemIssues = (item: ReferralItem) => computeValidationIssues(item.extracted);
  const readyItems = items.filter(i => i.status === "extracted" && getItemIssues(i).length === 0);
  const warningItems = items.filter(i => i.status === "extracted" && getItemIssues(i).length > 0);
  const submittedItems = items.filter(i => i.status === "submitted");
  const readyCount = readyItems.length + submittedItems.length;
  const warningCount = warningItems.length;

  return (
    <Container elevation={2}>
      <Text heading bold mb={3}>
        {t("form.batch_summary")}
      </Text>

      <Box mb={3}>
        <HFlex gap={3}>
          <Chip
            icon={<Check />}
            label={t(readyCount === 1 ? "form.batch_ready_count" : "form.batch_ready_count_plural", {
              count: readyCount,
            })}
            color="primary"
            variant="outlined"
          />
          {warningCount > 0 && (
            <Chip
              icon={<Warning style={{ color: orange[700], marginLeft: 4 }} />}
              label={t(warningCount === 1 ? "form.batch_warning_count" : "form.batch_warning_count_plural", {
                count: warningCount,
              })}
              style={{ borderColor: orange[500], color: orange[700], background: orange[50], paddingLeft: 4 }}
              variant="outlined"
            />
          )}
        </HFlex>
      </Box>

      {isSubmitting && (
        <Box mb={3}>
          <Typography variant="body2" gutterBottom>
            {t("form.batch_submitting", { current: submittedCount + 1, total: totalToSubmit })}
          </Typography>
          <LinearProgress variant="determinate" value={(submittedCount / totalToSubmit) * 100} />
        </Box>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("form.status")}</TableCell>
              <TableCell>{t("form.file")}</TableCell>
              <TableCell>{t("form.patient_name")}</TableCell>
              <TableCell>{t("form.nhs_number")}</TableCell>
              <TableCell>{t("form.site")}</TableCell>
              <TableCell>{t("form.pathway_ids")}</TableCell>
              <TableCell align="right">{t("buttons.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => {
              const hasWarning = getItemIssues(item).length > 0;
              const canEdit = !isSubmitting && item.status !== "submitted";
              return (
                <StyledTableRow
                  key={item.id}
                  $hasWarning={hasWarning && item.status !== "submitted"}
                  $clickable={canEdit}
                  onClick={() => canEdit && onEditItem(index)}
                >
                  <TableCell>
                    {item.status === "extracted" && !hasWarning && (
                      <Chip size="small" label={t("form.ready")} color="primary" />
                    )}
                    {item.status === "extracted" && hasWarning && (
                      <Chip
                        size="small"
                        icon={<Warning style={{ color: orange[700], fontSize: 16 }} />}
                        label={t("form.needs_review")}
                        style={{ borderColor: orange[500], color: orange[700], paddingLeft: 4 }}
                        variant="outlined"
                      />
                    )}
                    {item.status === "submitted" && (
                      <Chip
                        size="small"
                        icon={<Check style={{ color: "white" }} />}
                        label={t("form.submitted")}
                        style={{ background: green[500], color: "white" }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap style={{ maxWidth: 150 }}>
                      {item.file.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.extracted?.patient_name || "-"}</TableCell>
                  <TableCell>{item.extracted?.nhs_number || "-"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap style={{ maxWidth: 120 }}>
                      {item.extracted?.site || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap style={{ maxWidth: 100 }}>
                      {item.extracted?.pathway_ids || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    {canEdit && (
                      <HFlex gap={1}>
                        <IconButton size="small" onClick={() => onEditItem(index)} title={t("buttons.edit")}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => onRemove(item.id)} title={t("buttons.remove")}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </HFlex>
                    )}
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={4} display="flex" justifyContent="space-between">
        <Button startIcon={<ChevronLeft />} onClick={onBack} disabled={isSubmitting}>
          {t("buttons.back_to_review")}
        </Button>
        <ProgressButton
          loading={false}
          disabled={isSubmitting || items.filter(i => i.status === "extracted").length === 0}
          endIcon={<Send width={20} />}
          onClick={onSubmit}
        >
          {isSubmitting
            ? t("form.batch_submitting_button")
            : t("buttons.submit_all", { count: items.filter(i => i.status === "extracted").length })}
        </ProgressButton>
      </Box>
    </Container>
  );
};
